/**
 * 把「用户点了什么」翻译成「哪一段源码」，并且说清楚依据。
 *
 * 关键事实：表格里的人名、金额、门店名来自后端数据，前端源码里根本没有这些字。
 * 所以定位分三级：
 *   1. 原文命中——点的是按钮/菜单/表头这类写在源码里的文案；
 *   2. 表头定位——点的是数据格，用同一列的表头文字反查渲染这段表格的代码；
 *   3. 主文件兜底——都找不到，明确告诉用户，而不是硬塞一个看起来像的位置。
 */
import { cloudFind } from "@/system/cloud";
import { findModule, locateAcross } from "@/system/modules";

export type LocateQuality = "exact" | "header" | "fallback" | "none";

export type LocateResult = {
  /** 本地模式是 SourceDoc.id，云端模式是仓库里的相对路径 */
  docId: string | null;
  line: number | null;
  quality: LocateQuality;
  /** 给人看的一句话：为什么定位到这里 */
  basis: string;
};

const TABLE_ANCHORS = ["<table", "<tbody", ".map(", "map("];

function short(text: string, max = 40): string {
  const value = text.replace(/\s+/g, " ").trim();
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function localTableAnchor(docs: { doc: { id: string }; code: string }[], moduleId: string): LocateResult | null {
  const primary = findModule(moduleId).primary;
  const order = [...docs].sort((a, b) => Number(b.doc.id === primary) - Number(a.doc.id === primary));
  for (const anchor of TABLE_ANCHORS) {
    const hit = locateAcross(order as never, anchor);
    if (hit) {
      return {
        docId: hit.doc.id,
        line: hit.line,
        quality: "fallback",
        basis: `这段文字在源码里找不到（多半是后端数据），先带你看渲染表格的位置：${short(hit.text, 32)}`,
      };
    }
  }
  return null;
}

export function resolveLocal(
  docs: { doc: { id: string; path: string }; code: string }[],
  moduleId: string,
  target: string,
  anchor: string | null,
): LocateResult {
  const direct = locateAcross(docs as never, target);
  if (direct) {
    return {
      docId: direct.doc.id,
      line: direct.line,
      quality: "exact",
      basis: `源码原文命中：${short(direct.text, 48)}`,
    };
  }

  if (anchor) {
    const byHeader = locateAcross(docs as never, anchor);
    if (byHeader) {
      return {
        docId: byHeader.doc.id,
        line: byHeader.line,
        quality: "header",
        basis: `「${short(target, 16)}」是数据，源码里没有；按表头「${anchor}」定位到这一列的渲染代码`,
      };
    }
  }

  const fallback = localTableAnchor(docs as never, moduleId);
  if (fallback) return fallback;

  return {
    docId: findModule(moduleId).primary,
    line: null,
    quality: "none",
    basis: `没找到这段文字对应的代码，当前展示模块主文件`,
  };
}

export async function resolveCloud(
  moduleId: string,
  target: string,
  anchor: string | null,
): Promise<LocateResult> {
  const tries: string[] = [target];
  if (anchor && anchor !== target) tries.push(anchor);

  for (const [index, query] of tries.entries()) {
    if (query.trim().length < 2) continue;
    let hits;
    try {
      hits = await cloudFind(query, moduleId, 5);
    } catch {
      return { docId: null, line: null, quality: "none", basis: "云端检索失败，请检查代码服务是否在线" };
    }
    const top = hits[0];
    if (!top) continue;

    if (index === 0 && top.score >= 6) {
      return {
        docId: top.path,
        line: top.line,
        quality: "exact",
        basis: `源码原文命中：${short(top.text, 48)}`,
      };
    }
    if (index === 0 && top.score >= 4) {
      return {
        docId: top.path,
        line: top.line,
        quality: "exact",
        basis: `忽略空格后命中：${short(top.text, 48)}`,
      };
    }
    if (index === 1) {
      return {
        docId: top.path,
        line: top.line,
        quality: "header",
        basis: `「${short(target, 16)}」是数据，源码里没有；按表头「${anchor ?? query}」定位到这一列的渲染代码`,
      };
    }
  }

  return {
    docId: null,
    line: null,
    quality: "none",
    basis: `没找到「${short(target, 16)}」对应的代码——这类文字多半来自后端数据，不是写死在前端的`,
  };
}
